import { ref } from "vue";
import { useI18n } from "vue-i18n";
import { apiService } from "@/services/apiService";
import { useAuthStore } from "@/stores/auth";
import { PERMISSIONS } from "@/constants/permissions";

export function useProduct() {
  const { t } = useI18n();
  const { hasScope } = useAuthStore();

  const error = ref(null);
  const loading = ref(false);
  const products = ref([]);
  
  const canReadProduct = ref(hasScope(PERMISSIONS.PRODUCT_READ));
  
  const fetchProductById = async (id) => {
    if (!canReadProduct.value) {
      const errorObject = new Error(t("notAllowed"));
      errorObject.detail = t("crudMessages.readDenied", {
        resource: "products",
      });
      error.value = errorObject.detail;
      return null;
    }
    loading.value = true;
    error.value = null;
    try {
      return await apiService.get(`/products/${id}`);
    } catch (err) {
      error.value =
        err?.detail ||
        err?.message ||
        t("crudMessages.fetchError", { resource: "product" });
      return null;
    } finally {
      loading.value = false;
    }
  };

  const fetchProducts = async (skip = 0, limit = 200) => {
    if (!canReadProduct.value) {
      const errorObject = new Error(t("notAllowed"));
      errorObject.detail = t("crudMessages.readDenied", {
        resource: "products",
      });
      error.value = errorObject.detail;
      return [];
    }
    loading.value = true;
    error.value = null;
    try {
      const response = await apiService.get("/products/", { skip, limit });
      products.value = Array.isArray(response) ? response : [];
      return products.value;
    } catch (err) {
      error.value =
        err?.detail ||
        err?.message ||
        t("crudMessages.fetchError", { resource: "products" });
      products.value = [];
      return products.value;
    } finally {
      loading.value = false;
    }
  };

  const fetchProductsByQuery = async (q = "", skip = 0, limit = 100) => {
    if (!canReadProduct.value) {
      const errorObject = new Error(t("notAllowed"));
      errorObject.detail = t("crudMessages.readDenied", {
        resource: "products",
      });
      error.value = errorObject.detail;
      return [];
    }
    loading.value = true;
    error.value = null;
    try {
      return await apiService.get("/products/search", {
        q,
        skip,
        limit,
      });
    } catch (err) {
      error.value = err?.message || "Error fetching product";
      return null;
    } finally {
      loading.value = false;
    }
  };

  const calculateProductPrice = (
    item,
    selectedOptions = [],
    selectedExtras = []
  ) => {
    if (item) {
      const basePrice = item.price || 0;
      let addonsPrice = 0;

      if (Object.keys(selectedOptions).length > 0) {
        Object.keys(selectedOptions).forEach((addonName) => {
          const selectedOption = selectedOptions[addonName];
          if (selectedOption?.value) {
            addonsPrice += selectedOption.value;
          }
        });
      }

      if (selectedExtras.length > 0) {
        selectedExtras.forEach((extra) => {
          if (extra.value) {
            addonsPrice += extra.value;
          }
        });
      }

      return basePrice + addonsPrice;
    }
    return 0;
  };

  const determineProductBasePrice = (product) => {
    if (!product) return 0;
    let price = product.price;

    if (product.addons && product.addons.length > 0) {
      product.addons.forEach((addon) => {
        if (
          addon.isRequired &&
          addon.options &&
          addon.options.length > 0
        ) {
          price += addon.options[0].value;
        }
      });
    }
    return price;
  };

  return {
    calculateProductPrice,
    canReadProduct,
    determineProductBasePrice,
    error,
    fetchProductById,
    fetchProducts,
    fetchProductsByQuery,
    loading,
    products,
  };
}
